import { ExpandLess, ExpandMore, FilterList } from '@mui/icons-material'
import {
  Box,
  Button,
  Collapse,
  IconButton,
  Stack,
  TableCell,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { generatePath, useNavigate } from 'react-router-dom'
import FilterCostSummary from '../../components/FilterMenu/CostSummary/FilterCostSummary'
import { costTypeOptions } from '../../components/Sidebar/cost/CostViewForm'
import CostPieChartSidebar from '../../components/Sidebar/costPieChart/CostPieChartSidebar'
import DataTable from '../../components/Table/DataTable'
import defaultProps from '../../config/defaultProps'
import navigation from '../../config/navigation'
import { useAppDispatch, useAppSelector } from '../../store/redux/hooks'
import { useGetCostsQuery } from '../../store/redux/reducers/costSlice'
import { getSelectedDate, monthPickerActions } from '../../store/redux/reducers/monthPickerSlice'
import { pageHeaderActions } from '../../store/redux/reducers/pageHeaderSlice'
import { PageIndex, setPageIndex, setPageName } from '../../store/redux/reducers/pageNameSlice'
import { RequestProps } from '../../store/types'
import LocalStorageTypes from '../../store/types/enums/LocalStorageTypes'
import SessionStorageKeys from '../../store/types/enums/SessionStorageKeys'
import { Column, Row } from '../../store/types/utils/DataTableUtils'
import usePersistedPage from '../../store/utils/usePersistedPage'
import { formatStringDate } from '../../store/utils/dateUtils'

const CostSummaryView = () => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const theme = useTheme()
  const navigate = useNavigate()
  const selectedDate = useAppSelector(state => getSelectedDate(state))
  const [expandedTile, setExpandedTile] = useState<string | null>(null)

  const handleToggleCollapse = (id: string) => {
    setExpandedTile(prev => (prev === id ? null : id))
  }

  useEffect(() => {
    dispatch(setPageName(t('page.name.costSummary')))
    dispatch(setPageIndex(PageIndex.COST_SUMMARY))

    dispatch(
      pageHeaderActions.setButtons({
        monthPicker: true,
      })
    )

    dispatch(monthPickerActions.setDate(dayjs().toString()))

    return () => {
      dispatch(pageHeaderActions.setInitialState())
    }
  }, [dispatch, t])

  const [isFilteringMenuOpen, setIsFilteringMenuOpen] = useState(false)
  const isDownMd = useMediaQuery(theme.breakpoints.down('md'))
  const isDownSm = useMediaQuery(theme.breakpoints.down('sm'))
  const [page, setPage] = usePersistedPage(SessionStorageKeys.CostSummaryTablePage)
  const [requestProps, setRequestProps] = useState<RequestProps>(defaultProps)

  const { data: costs, isLoading } = useGetCostsQuery(
    {
      yearMonth: dayjs(selectedDate).format('YYYY-MM'),
      ...requestProps,
    },
    {
      skip: !selectedDate,
    }
  )

  const columns: Column[] = [
    { id: 'total', label: t('page.costSummary.table.header.total') },
    { id: 'amount', label: t('page.costSummary.table.header.amount') },
    { id: 'quantity', label: t('page.costSummary.table.header.quantity') },
    { id: 'type', label: t('page.costSummary.table.header.type') },
    { id: 'project', label: t('page.costSummary.table.header.project') },
    { id: 'author', label: t('page.costSummary.table.header.payer') },
    { id: 'principal', label: t('page.costSummary.table.header.principal') },
    { id: 'description', label: t('page.costSummary.table.header.description') },
    { id: 'startDate', label: t('page.costSummary.table.header.startingDate') },
    { id: 'endDate', label: t('page.costSummary.table.header.endingDate') },
  ]

  const renderCostSummaryTile = (costs: Row, labelId: string) => {
    const id = costs.id
    return (
      <Stack direction="column" key={labelId}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'background.elevation8',
              padding: '4px 8px',
              borderRadius: 1,
            }}
          >
            <Typography variant="body2" color="textSecondary">
              {t(
                costTypeOptions.find(option => option.value === costs.type)?.label ||
                  'components.costSidebar.types.unknown'
              )}
            </Typography>
          </Box>
          <Typography variant="body2" color="textSecondary">
            {costs.startDate || '-'} - {costs.endDate || '-'}
          </Typography>
        </Stack>
        <Typography variant="h6" noWrap>
          {`${(costs.amount * costs.quantity).toFixed(2)} zł`}
        </Typography>
        <Stack direction="row" spacing="10px" alignItems="center">
          <Typography variant="body1" fontWeight={500}>
            {t('page.costSummary.table.header.amount')}:
          </Typography>
          <Typography color="textSecondary" variant="body1">
            {`${costs.amount.toFixed(2)} zł`}
          </Typography>
        </Stack>
        <Stack direction="row" spacing="10px" alignItems="center">
          <Typography variant="body1" fontWeight={500}>
            {t('page.costSummary.table.header.quantity')}:
          </Typography>
          <Typography color="textSecondary" variant="body1">
            {costs.quantity}
          </Typography>
        </Stack>
        <Stack direction="column" justifyContent="space-between">
          <Box>
            <Stack direction="row" spacing="10px" alignItems="center">
              <Typography variant="body1" fontWeight={500}>
                {t('page.costSummary.table.header.payer')}:
              </Typography>
              <Typography
                color="textSecondary"
                variant="body1"
                sx={{
                  cursor: 'pointer',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
                onClick={() => navigate(generatePath(navigation.userProfile, { id: costs.author.id }))}
              >
                {`${costs.author.firstName || ''} ${costs.author.lastName || ''}`}
              </Typography>
            </Stack>
          </Box>
          {costs.project && (
            <Box>
              <Stack direction="row" spacing="10px" alignItems="center">
                <Typography variant="body1" fontWeight={500}>
                  {t('page.costSummary.table.header.project')}:
                </Typography>
                <Typography
                  color="textSecondary"
                  variant="body1"
                  sx={{
                    cursor: 'pointer',
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                  onClick={() => navigate(generatePath(navigation.projectDetails, { id: costs.project.id }))}
                >
                  {costs.project.acronym || costs.project.name}
                </Typography>
              </Stack>
            </Box>
          )}
          <Box>
            <Stack direction="row" spacing="10px" alignItems="center">
              <Typography variant="body1" fontWeight={500}>
                {t('page.costSummary.table.header.principal')}:
              </Typography>
              <Typography color="textSecondary" variant="body1">
                {costs.principal}
              </Typography>
            </Stack>
          </Box>
          {costs.description && (
            <Box>
              {costs.description.length > 36 ? (
                <>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
                      {!(expandedTile === id) && (
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          sx={{
                            cursor: 'pointer',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                          }}
                        >
                          {costs.description}
                        </Typography>
                      )}
                    </Box>
                    <IconButton size="small" onClick={() => handleToggleCollapse(id)}>
                      {expandedTile === id ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                  </Stack>
                  <Collapse in={expandedTile === id} timeout="auto" unmountOnExit>
                    <Box
                      sx={{
                        mt: 1,
                        pl: 2,
                        whiteSpace: 'normal',
                        wordWrap: 'break-word',
                        overflowWrap: 'break-word',
                      }}
                    >
                      <Typography variant="body2">{costs.description}</Typography>
                    </Box>
                  </Collapse>
                </>
              ) : (
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{
                    whiteSpace: 'normal',
                    wordWrap: 'break-word',
                    overflowWrap: 'break-word',
                  }}
                >
                  {costs.description}
                </Typography>
              )}
            </Box>
          )}
        </Stack>
      </Stack>
    )
  }

  const renderRow = (costs: Row) => {
    return (
      <>
        <TableCell>
          <Typography fontWeight={500}>{`${(costs.amount * costs.quantity).toFixed(2)} zł`}</Typography>
        </TableCell>
        <TableCell>
          <Typography>{`${costs.amount.toFixed(2)} zł`}</Typography>
        </TableCell>
        <TableCell>
          <Typography>{costs.quantity}</Typography>
        </TableCell>
        <TableCell>
          <Typography>
            {t(
              costTypeOptions.find(option => option.value === costs.type)?.label ||
                'components.costSidebar.types.unknown'
            )}
          </Typography>
        </TableCell>
        <TableCell>
          {costs.project ? (
            <Typography
              sx={{
                cursor: 'pointer',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
              onClick={() => navigate(generatePath(navigation.projectDetails, { id: costs.project.id }))}
            >
              {costs.project.acronym || costs.project.name}
            </Typography>
          ) : (
            <Typography>-</Typography>
          )}
        </TableCell>
        <TableCell sx={{ height: 52 }}>
          <Stack
            direction={'row'}
            sx={{ alignItems: 'center', gap: 1, cursor: 'pointer' }}
            onClick={() => navigate(generatePath(navigation.userProfile, { id: costs.author.id }))}
          >
            <Typography
              sx={{
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              {costs.author.firstName + ' ' + costs.author.lastName}
            </Typography>
          </Stack>
        </TableCell>
        <TableCell>
          <Typography>{costs.principal}</Typography>
        </TableCell>

        <TableCell
          sx={{
            maxWidth: 250,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          <Tooltip placement={'bottom-start'} title={costs.description}>
            <Typography>{costs.description ? costs.description : '-'}</Typography>
          </Tooltip>
        </TableCell>
        <TableCell>
          <Typography>{costs.startDate !== null ? formatStringDate(costs.startDate) : '-'}</Typography>
        </TableCell>
        <TableCell>
          <Typography>{costs.endDate !== null ? formatStringDate(costs.endDate) : '-'}</Typography>
        </TableCell>
      </>
    )
  }

  return (
    <Stack
      sx={{
        px: { xs: 0, md: 2 },
        gap: 4,
        paddingBottom: 3,
      }}
    >
      <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
        <Typography variant={'h5'}>{t('page.costSummary.allCosts')}</Typography>
        {isDownSm && <CostPieChartSidebar date={selectedDate} resize={isDownMd} />}
      </Stack>
      <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
        <Button
          variant="contained"
          color="secondary"
          startIcon={<FilterList />}
          className={isFilteringMenuOpen ? 'selected' : ''}
          onClick={() => setIsFilteringMenuOpen(!isFilteringMenuOpen)}
          fullWidth={isDownSm}
        >
          {t('page.users.buttons.filter')}
        </Button>
        {!isDownSm && <CostPieChartSidebar date={selectedDate} resize={isDownMd} />}
      </Stack>
      <Box
        display={'flex'}
        flexDirection={{ xs: 'column', md: 'row' }}
        justifyContent={'space-between'}
        gap={isFilteringMenuOpen ? 2 : 0}
        height="fit-content"
      >
        {isDownMd && (
          <Collapse in={isFilteringMenuOpen} orientation={'vertical'}>
            <FilterCostSummary requestProps={requestProps} setRequestProps={setRequestProps} setPage={setPage} />
          </Collapse>
        )}

        {!isDownMd && (
          <Collapse in={isFilteringMenuOpen} orientation={'horizontal'}>
            <FilterCostSummary requestProps={requestProps} setRequestProps={setRequestProps} setPage={setPage} />
          </Collapse>
        )}
        <DataTable
          renderTileContent={(costs: Row, labelId: string) => renderCostSummaryTile(costs, labelId)}
          singleSelection={false}
          columns={columns}
          data={costs?.content || []}
          renderRow={renderRow}
          totalElements={costs?.totalElements || 0}
          setRequestProps={setRequestProps}
          translationForNotFound={t('page.costSummary.table.notFound')}
          page={page}
          setPage={setPage}
          isLoading={isLoading}
          removeCheckboxes
          hoverOff
          isLastCellNormal
          commonRowsPerPage={
            localStorage.getItem(LocalStorageTypes.CostSummaryRows)
              ? parseInt(localStorage.getItem(LocalStorageTypes.CostSummaryRows)!)
              : undefined
          }
          localStorageRowsName={LocalStorageTypes.CostSummaryRows}
        />
      </Box>
    </Stack>
  )
}

export default CostSummaryView
